import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Check, X as XIcon, ShieldCheck, Paintbrush, Gauge, Phone, Camera, PackageCheck, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact';
interface WarrantyPageProps {
  onNavigate: (page: PageView, productId?: number) => void;
}
const faqs = [{
  q: 'Is the warranty transferable?',
  a: "Yes. Our warranty transfers to subsequent owners with proof of original purchase. Contact us with the original order number and we'll update our records."
}, {
  q: 'Do I need to register my product?',
  a: 'No registration is required. Your warranty is automatically active from the date of purchase. We recommend keeping your receipt or order confirmation email for your records.'
}, {
  q: 'What if I installed the product myself?',
  a: 'Self-installation is fully covered under warranty, provided the installation was performed according to our published instructions. We recommend documenting your install with photos.'
}, {
  q: 'How long does a warranty claim take?',
  a: 'Most claims are resolved within 5-10 business days. Simple replacements ship within 48 hours of approval. Claims requiring inspection may take slightly longer.'
}, {
  q: 'Does the warranty cover paint and powder coat?',
  a: 'Finish is covered for 1 year against peeling, flaking, and bubbling under normal use. Surface scratches from trail use and rock chips are considered normal wear.'
}];
export function WarrantyPage({
  onNavigate
}: WarrantyPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-687">
      <Header data-id="element-688" />
      <Navigation onNavigate={onNavigate} data-id="element-689" />

      <main className="flex-grow" data-id="element-690">
        {/* Hero */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-691">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-692" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-693" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-694">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-695">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-696">
                Home
              </button>
              <span data-id="element-697">/</span>
              <span className="text-white" data-id="element-698">Warranty</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-699">
              Warranty <span className="text-primary" data-id="element-700">Information</span>
            </h1>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-id="element-701">
          {/* Intro */}
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-16" data-id="element-702">
            Every Pro One Design product is engineered to outlast the terrain
            you throw at it. We stand behind our work with industry-leading
            warranty coverage because we know what goes into every weld, every
            machined surface, and every finished part.
          </p>

          {/* Coverage Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-703">
            <div className="bg-white border border-gray-200 p-8 border-t-4 border-t-primary" data-id="element-704">
              <ShieldCheck className="w-10 h-10 text-primary mb-4" data-id="element-705" />
              <h3 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-2" data-id="element-706">
                Structural
              </h3>
              <p className="text-3xl font-heading font-bold text-primary italic mb-3" data-id="element-707">
                Lifetime
              </p>
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-708">
                All structural components including crossmembers, knuckles,
                control arms, and mounting brackets are warranted against
                defects for the life of the product.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-8 border-t-4 border-t-charcoal" data-id="element-709">
              <Paintbrush className="w-10 h-10 text-charcoal mb-4" data-id="element-710" />
              <h3 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-2" data-id="element-711">
                Finish
              </h3>
              <p className="text-3xl font-heading font-bold text-primary italic mb-3" data-id="element-712">
                1 Year
              </p>
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-713">
                Powder coat and anodized finishes are covered against peeling,
                flaking, and bubbling for one year from date of purchase under
                normal use conditions.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-8 border-t-4 border-t-chrome" data-id="element-714">
              <Gauge className="w-10 h-10 text-chrome mb-4" data-id="element-715" />
              <h3 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-2" data-id="element-716">
                Shocks
              </h3>
              <p className="text-3xl font-heading font-bold text-primary italic mb-3" data-id="element-717">
                2 Years
              </p>
              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-718">
                All shock absorbers and coilovers are warranted against leaking
                seals, bent shafts, and internal failures for two years from
                purchase.
              </p>
            </div>
          </div>

          {/* Covered / Not Covered */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20" data-id="element-719">
            <div data-id="element-720">
              <h2 className="text-3xl font-heading font-bold text-charcoal uppercase italic mb-6 border-l-4 border-primary pl-4" data-id="element-721">
                What's Covered
              </h2>
              <div className="space-y-3" data-id="element-722">
                {['All structural welds and joints', 'Mounting hardware and brackets', 'Manufacturer defects in materials', 'Premature component failure', 'Bent or broken parts under normal use', 'Shock seal failure and internal leaks'].map(item => <div key={item} className="flex items-start gap-3 bg-white p-3 border-l-2 border-green-500" data-id="element-723">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-724" />
                    <span className="text-gray-700 font-medium text-sm" data-id="element-725">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            <div data-id="element-726">
              <h2 className="text-3xl font-heading font-bold text-charcoal uppercase italic mb-6 border-l-4 border-gray-300 pl-4" data-id="element-727">
                What's Not Covered
              </h2>
              <div className="space-y-3" data-id="element-728">
                {['Normal wear and tear from use', 'Improper installation or modification', 'Racing, competition, or commercial use', 'Unauthorized modifications to the product', 'Cosmetic damage (scratches, chips, dents)', 'Damage from accidents or misuse'].map(item => <div key={item} className="flex items-start gap-3 bg-white p-3 border-l-2 border-gray-300" data-id="element-729">
                    <XIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-730" />
                    <span className="text-gray-500 text-sm" data-id="element-731">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* How to File */}
          <div className="bg-charcoal text-white p-10 mb-20 border-t-4 border-primary" data-id="element-732">
            <h2 className="text-3xl font-heading font-bold uppercase italic mb-10 text-center" data-id="element-733">
              How to File a <span className="text-primary" data-id="element-734">Claim</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-735">
              <div className="text-center" data-id="element-736">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 font-heading font-bold text-2xl italic" data-id="element-737">
                  1
                </div>
                <Phone className="w-8 h-8 text-primary mx-auto mb-3" data-id="element-738" />
                <h3 className="text-xl font-heading font-bold uppercase italic mb-2" data-id="element-739">
                  Contact Us
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-740">
                  Call (559) 123-4567 or email warranty@proonedesign.com with
                  your order number and a description of the issue.
                </p>
              </div>
              <div className="text-center" data-id="element-741">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 font-heading font-bold text-2xl italic" data-id="element-742">
                  2
                </div>
                <Camera className="w-8 h-8 text-primary mx-auto mb-3" data-id="element-743" />
                <h3 className="text-xl font-heading font-bold uppercase italic mb-2" data-id="element-744">
                  Inspection
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-745">
                  Send photos of the issue. In some cases we may request the
                  part be shipped to us for a hands-on inspection.
                </p>
              </div>
              <div className="text-center" data-id="element-746">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 font-heading font-bold text-2xl italic" data-id="element-747">
                  3
                </div>
                <PackageCheck className="w-8 h-8 text-primary mx-auto mb-3" data-id="element-748" />
                <h3 className="text-xl font-heading font-bold uppercase italic mb-2" data-id="element-749">
                  Resolution
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-750">
                  Approved claims receive a replacement or repair at no cost.
                  Most claims are resolved within 5-10 business days.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div data-id="element-751">
            <h2 className="text-3xl font-heading font-bold text-charcoal uppercase italic mb-8 border-l-4 border-primary pl-4" data-id="element-752">
              Frequently Asked Questions
            </h2>
            <div className="space-y-2" data-id="element-753">
              {faqs.map((faq, idx) => <div key={idx} className="bg-white border border-gray-200 overflow-hidden" data-id="element-754">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left" data-id="element-755">
                    <span className="font-bold text-charcoal pr-4" data-id="element-756">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-757" />
                  </button>
                  <AnimatePresence data-id="element-758">
                    {openFaq === idx && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-759">
                        <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4" data-id="element-760">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-761" />
    </div>;
}